import type { ActivePaymentRelationType } from '#app/constants/activePaymentRelationTypes';
import type { ActivePayoutRecord } from '#app/pages/Investing/Calendar/types/activePayout';
import type { ApiResponse } from '#app/types/api';

/**
 * Типы ответа API GET active/payments (сырая вложенная структура с бэка).
 *
 * Контракт:
 *   data[DD.MM.YYYY][TICKER][relation_type][account_id] → строка выплаты
 *
 * Пример:
 *   data["18.07.2025"]["SBER"]["active.dividend"]["235020"] → { plan_sum, count, ... }
 */

/** Ключ даты в ответе: DD.MM.YYYY */
export type ApiPaymentsDateKey = string;

/** Ключ инструмента: тикер (SBER) или ISIN */
export type ApiPaymentsInstrumentKey = string;

/** Ключ субсчёта в объекте (строка с числовым id) */
export type ApiPaymentsAccountKey = string;

/** relation для active.dividend / active.coupon (заголовок active-dividend / active-coupon). */
export interface ApiPaymentActiveRelation {
  id: number;
  catalog_hub_id?: number;
  currency_id?: number;
  sum?: number;
  paid_at?: string;
  note?: string | null;
  [key: string]: unknown;
}

/** relation для moex.* / spb.* (биржевой план, distribution по id выплаты биржи). */
export interface ApiPaymentMoexRelation {
  id: number;
  value?: number;
  currencyid?: string;
  moex_stock_id?: number;
  [key: string]: unknown;
}

export type ApiPaymentRelation = ApiPaymentActiveRelation | ApiPaymentMoexRelation;

/** Объект инструмента из каталога (облигация/акция) внутри payment.item */
export interface ApiPaymentCatalogItem {
  id: number;
  secid: string;
  symbol: string;
  shortname?: string;
  isin?: string;
  regnumber?: string;
  name?: string;
  type?: string;
  currency_id?: number;
  facevalue?: number;
  faceunit?: string;
  issuedate?: string;
  matdate?: string;
  couponpercent?: number;
  couponfrequency?: number;
  group?: string;
  gosreg?: string;
  icon?: string;
  is_traded?: string;
  lotsize?: number;
  ticker?: string;
  [key: string]: unknown;
}

/**
 * Одна строка выплаты по субсчёту (лист вложенности active/payments).
 * @example SBER active.dividend account 235020
 */
export interface ApiPaymentRow {
  /** Фактически выплаченная сумма */
  sum: number;
  original_sum: number;
  /** Плановая сумма выплаты */
  plan_sum: number;
  original_plan_sum: number;
  count?: number;
  trades_count?: number;
  account_id: number;
  currency_id: number;
  relation_type: ActivePaymentRelationType;
  ticker_hub_id: number;
  lotsize?: number;
  name_text: string;
  type_id: number;
  /** Id active-dividend / active-coupon после сохранения (может отсутствовать в плане) */
  id?: number;
  /** Id заголовка active-dividend / active-coupon (distribution relation_id) */
  relation_id?: number;
  /** Заголовок выплаты: active-dividend/coupon или биржевая выплата (moex.*) */
  relation?: ApiPaymentRelation | null;
  /** Зачислены ли деньги по строке (если бэкенд отдаёт явно) */
  is_confirmed?: boolean | number | string;
  item?: ApiPaymentCatalogItem | null;
  isin?: string;
  ticker?: string;
  date?: string;
  company_name?: string;
  issuer_name?: string;
  payment_type?: string;
  [key: string]: unknown;
}

/** @deprecated Используйте ApiPaymentRow */
export type ApiPaymentRaw = ApiPaymentRow;

/** account_id → строка выплаты */
export type ApiPaymentsByAccount = Record<ApiPaymentsAccountKey, ApiPaymentRow>;

/** relation_type → account_id → строка (новый контракт) */
export type ApiPaymentsByRelationType = Partial<
  Record<ActivePaymentRelationType, ApiPaymentsByAccount>
>;

/** Тикер/ISIN → relation_type → account_id (или legacy: account_id напрямую) */
export type ApiPaymentsByInstrument = Record<
  ApiPaymentsInstrumentKey,
  ApiPaymentsByRelationType | ApiPaymentsByAccount
>;

/** DD.MM.YYYY → тикер → … */
export type ApiPaymentsData = Record<ApiPaymentsDateKey, ApiPaymentsByInstrument>;

/** Полный ответ GET active/payments: { meta, result, data, count_queries } */
export type ActivePaymentsApiResponse = ApiResponse<ApiPaymentsData>;

/** @deprecated Используйте ApiPaymentsData */
export type ApiPaymentsResponse = ApiPaymentsData;

/**
 * Извлекает data из ответа active/payments.
 * Api-клиент может отдать уже data или обёртку { result, data }.
 */
export function unwrapActivePaymentsData(payload: unknown): ApiPaymentsData {
  if (payload == null || typeof payload !== 'object') {
    return {};
  }

  if (Array.isArray(payload)) {
    return {};
  }

  const maybeEnvelope = payload as ActivePaymentsApiResponse;
  if (
    maybeEnvelope.data != null &&
    typeof maybeEnvelope.data === 'object' &&
    !Array.isArray(maybeEnvelope.data)
  ) {
    return maybeEnvelope.data;
  }

  return payload as ApiPaymentsData;
}

/**
 * Элемент массива выплат после transformPaymentsResponse.
 * Используется в CaseDividend (график/таблица), календаре.
 */
export interface CalendarPaymentItem {
  id: string;
  date: string;
  sum: number;
  plan_sum?: number | null;
  original_sum?: number | null;
  original_plan_sum?: number | null;
  currency_id?: number;
  currency?: string;
  isin?: string;
  ticker?: string;
  name_text?: string;
  type_id?: string | number;
  item?: ApiPaymentCatalogItem | null;
  company_name?: string;
  payment_type?: string;
  /** active.dividend | moex.dividend | spb.* | active.coupon | … */
  relation_type?: ActivePaymentRelationType | string;
  /** Биржевой план: любой morph кроме active.* (см. resolveActivePaymentExchangePlan) */
  from_exchange_plan?: boolean;
  active_account_id?: number | null;
  count?: number;
  lotsize?: number;
  catalog_hub_id?: number;
  /** Id active-dividend / active-coupon (заголовок выплаты) */
  relation_id?: number;
  /** Заголовок выплаты из active/payments (если бэкенд отдаёт relation) */
  relation?: ApiPaymentRelation | null;
  /** «Зачислить деньги» по строке, если бэкенд отдаёт явно */
  is_confirmed?: boolean | number | string;
  /** Сопоставленная выплата с active/index (legacy merge) */
  matchedPayout?: ActivePayoutRecord;
  matchedCatalogPayout?: ActivePayoutRecord;
  payoutSource?: 'active' | 'catalog';
  fromActiveEnrichment?: boolean;
  /** Исходные строки до агрегации по ISIN и дате */
  groupedMembers?: CalendarPaymentItem[];
  /** Id active-coupon / active-dividend по каждой доле */
  groupedRecordIds?: number[];
  /** Суммы для ячейки календаря (по валютам счетов) */
  display_amounts?: Array<{ paid_sum: number; plan_sum: number; currency: string }>;
  raw?: ApiPaymentRow;
}
